import React, { useState, useCallback } from 'react'
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import {
  Delete,
  OpenInNew,
  Star,
  StarBorder,
  Schedule,
  Share,
} from '@mui/icons-material'
import apiService from '../../services/ApiService'
const AnnotationCard = ({
  annotation,
  onDelete,
  onOpen,
  isMobile,
  showSnackbar,
  selectionMode = false,
  onUpdate,
  readOnly = false,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isStarring, setIsStarring] = useState(false)
  const lawNames = {
    'gcc-common-vat-agreement': 'GCC Common VAT Agreement',
    'gcc-common-customs-agreement': 'GCC Common Customs Agreement',
    'gcc-common-excise-agreement': 'GCC Common Excise Agreement',
    'uae-cit-fdl-47-of-2022': 'CIT (FDL No 47 of 2022, as amended)',
    'uae-dmtt-fdl-60-of-2023': 'DMTT (FDL No 60 of 2023)',
    'uae-indirecttax-law-vat-uaevat-8-of-2017-as-amended':
      'VAT (FDL No 8 of 2017, as amended)',
    'uae-tp-fdl-28-of-2022-as-amended':
      'Tax Procedures (FDL No 28 of 2022, as amended)',
    'uae-indirect-excise-7-of-2017-as-amended':
      'Excise Tax (FDL No 7 of 2017, as amended)',
    'uae-fatca-of-2024': 'FATCA (Foreign Account Tax Compliance Act) 2024',
    'uae-crs-federal-decree-48-of-2018':
      'CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
    'ksa-income-tax-law-of-2004': 'Income Tax Law 2004',
    'ksa-vat-law-m-113-of-2017': 'VAT Law 2017',
    'ksa-customs-law': 'Customs Law',
    'ksa-fatca-of-2017': 'FATCA (Foreign Account Tax Compliance Act) 2017',
    'ksa-excise-law-of-2019': 'Excise Law 2019',
    'ksa-zakat-law-of-1951': 'Zakat Collection Law 1951',
    'kuwait-draft-business-profit-tax-law-of-2024':
      'Draft Business Profit Tax Law 2024',
    'kuwait-kit-law-no-2-of-2008-amending-decree-no-3-of-1955':
      'Income Tax Law - Decree No. 3 of 1955',
    'kuwait-qdmtt-decree-law-157-of-2024': 'QDMTT Law - Decree No. 157 of 2024',
    'qatar-income-tax-law-24-of-2018': 'Income Tax Law 24 of 2018',
    'qatar-dmtt-fdl-22-of-2024': 'DMTT (FDL No 22 of 2024)',
    'qatar-tax-procedures-law': 'Tax Procedures Law',
    'qatar-excise-law-25-of-2018': 'Excise Law 2018',
    'qatar-fatca-of-2014-iga-m1':
      'FATCA (Foreign Account Tax Compliance Act) 2014',
    'oman-income-tax-law-royal-decree-28-of-2009':
      'Income Tax Law (Royal Decree No 28 of 2009, as amended)',
    'oman-vat-law-royal-decree-121-of-2020':
      'VAT Law - Royal Decree No. 121 of 2020',
    'oman-excise-tax-law-royal-decree-23-of-2019':
      'Excise Tax Law (Royal Decree No 23 of 2019)',
    'oman-fatca-of-2014-direct':
      'FATCA (Foreign Account Tax Compliance Act) 2014',
    'oman-cbcr-law': 'Country-by-Country Reporting Law',
    'bahrain-dmtt-law-11-of-2024':
      'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
    'bahrain-vat-law-48-of-2018': 'Bahrain VAT Law 48 of 2018',
  }

  // Decision type colors
  const decisionTypeColors = {
    'Cabinet Decision': {
      main: '#4e342e',
      light: '#efebe9',
    },
    'Ministerial Decision': {
      main: '#1976d2',
      light: '#e3f2fd',
    },
    'Federal Tax Authority Decision': {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    'Administrative Decision': {
      main: '#4e342e',
      light: '#efebe9',
    },
    'ZATCA Decision': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    Bylaws: {
      main: '#0097a7',
      light: '#e0f7fa',
    },
    'Executive Rules and Instructions': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Executive Regulations': {
      // ER
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Implementing Regulations': {
      // IR
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Executive Bylaws': {
      // EB
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Judicial Decision': {
      // JD
      main: '#673ab7',
      light: '#ede7f6',
    },
  }

  // Guide type colors
  const guideTypeColors = {
    'Public Clarification': {
      main: '#f44336',
      light: '#ffebee',
    },
    'Federal Tax Authority Guide': {
      main: '#3f51b5',
      light: '#e8eaf6',
    },
    'Zakat, Tax and Customs Authority': {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    'Federal Tax Authority Service Guide': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    'Foreign Account Tax Compliance Act Guide': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'User Manual': {
      main: '#ff9800',
      light: '#fff3e0',
    },
    'VAT Taxpayer Guide': {
      main: '#0097a7',
      light: '#e0f7fa',
    },
    'Tax Bulletin and Circular': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    Circular: {
      main: '#1976d2',
      light: '#e3f2fd',
    },
    'Oman Tax Authority': {
      main: '#37474f',
      light: '#eceff1',
    },
    'Qatar Tax Authority': {
      main: '#37474f',
      light: '#eceff1',
    },
    'National Bureau for Revenue': {
      main: '#37474f',
      light: '#eceff1',
    },
    'Foreign Account Tax Compliance Act FAQ and Flowchart': {
      main: '#3f51b5',
      light: '#e8eaf6',
    }
  }
  // Document base type colors
  const documentTypeColors = {
    // article: { main: '#1976d2', light: '#e3f2fd' },
    // decision: { main: '#4e342e', light: '#efebe9' },
    // guideline: { main: '#3f51b5', light: '#e8eaf6' },
    // guide: { main: '#3f51b5', light: '#e8eaf6' },
    // 'tax-treaty': { main: '#2e7d32', light: '#e8f5e9' },
    default: { main: '#9e9e9e', light: '#f5f5f5' },
  }
  // Map abbreviated type codes to full descriptive names (matching BookmarkCard)
  const getFullCategoryName = (abbreviation, documentType) => {
    if (documentType === 'decision') {
      const decisionMap = {
        CD: 'Cabinet Decision',
        MD: 'Ministerial Decision',
        FTA: 'Federal Tax Authority Decision',
        AD: 'Administrative Decision',
        ZD: 'ZATCA Decision',
        BL: 'Bylaws',
        ER: 'Executive Rules and Instructions',
        ERS: 'Executive Regulations',
        IR: 'Implementing Regulations',
        EB: 'Executive Bylaws',
        JD: 'Judicial Decision',
      }
      return decisionMap[abbreviation] || abbreviation
    }

    if (documentType === 'guide' || documentType === 'guideline') {
      const guideMap = {
        PC: 'Public Clarification',
        TPGPC: 'Public Clarification', // TPGPC1 is unique code for uae pc for cit and tp law
        GUIDE: 'Federal Tax Authority Guide',
        // GUIDE: 'Federal Tax Authority Service Guide',
        // GUIDE: 'Zakat, Tax and Customs Authority',
        // GUIDE: 'Foreign Account Tax Compliance Act Guide',
        // GUIDE: 'VAT Taxpayer Guide',
        // GUIDE: 'Oman Tax Authority',
        // GUIDE: 'Qatar Tax Authority',
        // GUIDE: 'National Bureau for Revenue',
        // GUIDE: 'Foreign Account Tax Compliance Act FAQ and Flowchart',
        MANUAL: 'User Manual',
        CIRCULAR: 'Circular',
        TBC: 'Tax Bulletin and Circular',
      }
      return guideMap[abbreviation] || 'Federal Tax Authority Guide' // Default for generic GUIDE
    }

    return abbreviation
  }
  const convertArrayToDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return null
    const [year, month, day, hour, minute, second, nanosecond] = dateArray
    return new Date(
      Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        Math.floor(nanosecond / 1000000),
      ),
    )
  }

  const getCountryFromDocId = (documentId) => {
    if (!documentId) return null

    const countryMap = {
      uae: 'UAE',
      ksa: 'KSA',
      kuwait: 'Kuwait',
      qatar: 'Qatar',
      oman: 'Oman',
      bahrain: 'Bahrain',
    }

    const firstPart = documentId.split('-')[0].toLowerCase()
    return countryMap[firstPart] || null
  }

  const getDocumentInfo = (documentId, documentType) => {
    const country = getCountryFromDocId(documentId)

    if (lawNames[documentId]) {
      return {
        country,
        lawName: lawNames[documentId],
        docNumber: null,
        decisionType: null,
      }
    }

    let lawName = ''
    let docNumber = null
    let decisionType = null

    if (documentType === 'article') {
      const parts = documentId.split('-article-')
      lawName = parts[0]
      docNumber = parts[1] ? `${parts[1]}` : null
    } else if (documentType === 'decision') {
      const match = documentId.match(
        /^(.+?)-(cd|md|fta|ad|zd|td|bl|er|ers|ir|eb|jd)-(.+)$/i,
      )
      if (match) {
        lawName = match[1]
        decisionType = match[2].toUpperCase()
        docNumber = `${decisionType} ${match[3]}`
      }
    } else if (documentType === 'guide' || documentType === 'guideline') {
      const match = documentId.match(
        /^(.+?)-(?:tpgpc|pc|guide|circular|manual)-(.+)$/i,
      )
      if (match) {
        lawName = match[1]
        decisionType = match[2].toUpperCase()
        docNumber = `${decisionType.toUpperCase()} ${match[3] || ''}`
      }
    } else if (documentType === 'tax-treaty') {
      const parts = documentId.split('-')
      if (parts.length >= 3) {
        const country1 = parts[0].toUpperCase()
        const country2 = parts[1].toUpperCase()
        lawName = `${country2}`
      }
    }

    if (!lawName) {
      lawName = documentId
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    return { country, lawName, docNumber, decisionType }
  }

  const getDocumentTypeLabel = (documentType) => {
    const typeMap = {
      article: 'Article',
      guide: 'Guide',
      decision: 'Decision',
      guideline: 'Guideline',
      'tax-treaty': 'Tax Treaty',
      blog: 'Blog',
    }
    return typeMap[documentType] || documentType
  }

  const getDocumentTypeColor = (documentType, decisionType) => {
    if (documentType === 'decision' && decisionType) {
      // Map abbreviation to full category name
      const fullCategoryName = getFullCategoryName(decisionType, 'decision')
      if (decisionTypeColors[fullCategoryName]) {
        return decisionTypeColors[fullCategoryName]
      }
    }

    if (
      (documentType === 'guide' || documentType === 'guideline') &&
      decisionType
    ) {
      // Map abbreviation to full category name
      const fullCategoryName = getFullCategoryName(decisionType, 'guide')
      if (guideTypeColors[fullCategoryName]) {
        return guideTypeColors[fullCategoryName]
      }
    }

    return documentTypeColors[documentType] || documentTypeColors.default
  }

  const getCategoryColor = (category) => {
    const colors = {
      general: '#9e9e9e',
      important: '#f44336',
      question: '#2196f3',
      definition: '#4caf50',
      example: '#ff9800',
      todo: '#9c27b0',
      interpretation: '#673ab7',
    }
    return colors[category] || colors.general
  }

  const createdDate = convertArrayToDate(annotation.createdAt)
  const updatedDate = convertArrayToDate(annotation.updatedAt)
  const { country, lawName, docNumber, decisionType } = getDocumentInfo(
    annotation.documentId,
    annotation.documentType,
  )
  const typeColors = getDocumentTypeColor(annotation.documentType, decisionType)
  const handleStarToggle = useCallback(
    async (e) => {
      e.stopPropagation()
      if (readOnly) return
      if (isStarring) return

      setIsStarring(true)
      try {
        const response = await apiService.put(
          `/api/v1/annotations/${annotation.id}/star`,
        )

        // Trigger parent to refresh the list
        if (onUpdate) {
          onUpdate(response)
        }

        const message = response.isImportant
          ? 'Annotation marked as important! ⭐'
          : 'Annotation unmarked as important'

        if (showSnackbar) {
          showSnackbar(message, 'success')
        }
      } catch (error) {
        console.error('Error toggling annotation star:', error)
        if (showSnackbar) {
          showSnackbar('Failed to update annotation', 'error')
        }
      } finally {
        setIsStarring(false)
      }
    },
    [annotation.id, isStarring, readOnly, showSnackbar],
  )
  const handleDeleteClick = (e) => {
    e.stopPropagation()
    if (readOnly) return
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false)
    onDelete(annotation.id)
  }
  // Open document button functionality, not open annotation url
  const handleOpenClick = (e) => {
    e.stopPropagation()
    if (annotation.pageUrl) {
      window.open(annotation.pageUrl, '_blank')
    } else {
      onOpen(annotation)
    }
  }

  const handleShare = useCallback(
    async (e) => {
      e.stopPropagation()

      // Create rich sharing content
      const lawName = lawNames[annotation.documentId] || annotation.documentId
      const docType = getDocumentTypeLabel(annotation.documentType)
      const country = getCountryFromDocId(annotation.documentId)

      // Build the share URL with annotation hash for direct navigation
      // const baseUrl =
      //   annotation.pageUrl ||
      //   `${window.location.origin}/annotations/${annotation.id}`
      // const shareUrl = annotation.pageUrl
      //   ? `${annotation.pageUrl}#annotation-${annotation.id}`
      //   : baseUrl
      // Use pageUrl directly without hash, since annotations are user-specific
      const shareUrl =
        annotation.pageUrl ||
        `${window.location.origin}/annotations/${annotation.id}`
      // Format the sharing text
      const isQuickHighlight =
        annotation.annotationContent === 'Quick Highlight'

      let shareText = `📌 Annotation on ${lawName}\n\n`

      if (country) {
        shareText += `🌍 ${country} | ${docType}\n\n`
      }

      shareText += `📝 Selected Text:\n"${annotation.selectedText}"\n\n`

      if (!isQuickHighlight && annotation.annotationContent) {
        shareText += `💭 Note:\n${annotation.annotationContent}\n\n`
      }

      if (annotation.tags && annotation.tags.length > 0) {
        shareText += `🏷️ Tags: ${annotation.tags.join(', ')}\n\n`
      }

      shareText += `🔗 View: ${shareUrl}`

      const shareData = {
        title: `Annotation: ${lawName}`,
        text: shareText,
        url: shareUrl, // This is used by native share API
      }

      try {
        if (navigator.share) {
          await navigator.share(shareData)
          if (showSnackbar) {
            showSnackbar('Annotation shared successfully!', 'success')
          }
        } else {
          // Fallback: Copy rich text to clipboard
          await navigator.clipboard.writeText(shareText)
          if (showSnackbar) {
            showSnackbar('Annotation details copied to clipboard!', 'success')
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          try {
            // Second fallback: Just copy the URL
            await navigator.clipboard.writeText(shareUrl)
            if (showSnackbar) {
              showSnackbar('Annotation link copied to clipboard!', 'success')
            }
          } catch (clipboardError) {
            if (showSnackbar) {
              showSnackbar('Failed to share annotation', 'error')
            }
          }
        }
      }
    },
    [annotation, showSnackbar],
  )

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown'

    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
    } else {
      return 'Just now'
    }
  }

  const isQuickHighlight = annotation.annotationContent === 'Quick Highlight'

  return (
    <>
      <Card
        sx={{
          mb: 2,
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: selectionMode ? 'none' : 'translateY(-2px)',
          },
          cursor: selectionMode ? 'default' : 'pointer',
          borderLeft: `5px solid ${typeColors.main}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(90deg, ${typeColors.light} 0%, rgba(255,255,255,0) 50%)`,
            opacity: 0.5,
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}
        onClick={() => !selectionMode && onOpen(annotation)}
      >
        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={2}>
            {/* First row: Metadata chips and action buttons */}
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              spacing={1}
              flexWrap='wrap'
            >
              <Stack
                direction='row'
                spacing={0.5}
                alignItems='center'
                flexWrap='wrap'
                sx={{ gap: 0.5 }}
              >
                <Chip
                  label={annotation.documentId}
                  size='small'
                  sx={{
                    bgcolor: 'white',
                    color: typeColors.main,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    height: '24px',
                    border: `1.5px solid ${typeColors.main}`,
                  }}
                />
                {/* {country && (
                  <Chip
                    label={country}
                    size='small'
                    sx={{
                      bgcolor: 'white',
                      color: typeColors.main,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      height: '24px',
                      border: `1.5px solid ${typeColors.main}`,
                    }}
                  />
                )}
                {lawName && (
                  <Chip
                    label={lawNames[lawName] || lawName}
                    size='small'
                    sx={{
                      bgcolor: 'white',
                      color: typeColors.main,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      height: '24px',
                      border: `1.5px solid ${typeColors.main}`,
                    }}
                  />
                )}
                <Chip
                  label={getDocumentTypeLabel(annotation.documentType)}
                  size='small'
                  sx={{
                    bgcolor: typeColors.main,
                    color: 'white',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    height: '24px',
                  }}
                />
                {docNumber && (
                  <Chip
                    label={docNumber}
                    size='small'
                    sx={{
                      bgcolor: typeColors.light,
                      color: typeColors.main,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      height: '24px',
                      border: `1px solid ${typeColors.main}`,
                    }}
                  />
                )} */}
              </Stack>

              {/* Action buttons */}
              <Stack direction='row' spacing={0.5}>
                {!readOnly && (
                  <Tooltip
                    title={
                      annotation.isImportant
                        ? 'Remove from favorites'
                        : 'Add to favorites'
                    }
                  >
                    <IconButton
                      size='small'
                      onClick={handleStarToggle}
                      disabled={isStarring}
                      sx={{
                        color: annotation.isImportant ? '#ffda1b' : '#6b7280',
                        '&:hover': {
                          bgcolor: 'rgba(255, 218, 27, 0.08)',
                          color: annotation.isImportant ? '#f59e0b' : '#ffda1b',
                        },
                      }}
                    >
                      {annotation.isImportant ? (
                        <Star fontSize='small' />
                      ) : (
                        <StarBorder fontSize='small' />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title='Share annotation'>
                  <IconButton
                    size='small'
                    onClick={handleShare}
                    sx={{
                      color: '#232536',
                      '&:hover': { bgcolor: 'rgba(35, 37, 54, 0.08)' },
                    }}
                  >
                    <Share fontSize='small' />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Open document'>
                  <IconButton
                    size='small'
                    onClick={handleOpenClick}
                    sx={{
                      color: '#232536',
                      '&:hover': { bgcolor: 'rgba(35, 37, 54, 0.08)' },
                    }}
                  >
                    <OpenInNew fontSize='small' />
                  </IconButton>
                </Tooltip>
                {!readOnly && (
                  <Tooltip title='Delete annotation'>
                    <IconButton
                      size='small'
                      onClick={handleDeleteClick}
                      sx={{
                        color: '#f44336',
                        '&:hover': { bgcolor: '#ffebee' },
                      }}
                    >
                      <Delete fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Second row: Last Updated - Time ago */}
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <Schedule sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant='caption' color='text.secondary'>
                <strong>Last Updated:</strong> {formatTimeAgo(updatedDate)}
              </Typography>
            </Stack>

            {/* Selected text preview with highlight color */}
            <Box>
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  mb: 0.5,
                  display: 'block',
                }}
              >
                Selected Text:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: annotation.highlightColor || '#fff3cd',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  "{annotation.selectedText}"
                </Typography>
              </Box>
            </Box>

            {/* Annotation note (if not quick highlight) */}
            {!isQuickHighlight && annotation.annotationContent && (
              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Note:
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.primary',
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {annotation.annotationContent}
                </Typography>
              </Box>
            )}

            {/* Category and Tags */}
            <Stack spacing={1.5}>
              {annotation.category &&
                annotation.category !== 'general' &&
                annotation.category !== 'all' && (
                  <Box>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Category:
                    </Typography>
                    <Chip
                      label={annotation.category}
                      size='small'
                      sx={{
                        bgcolor: getCategoryColor(annotation.category),
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        height: '24px',
                        textTransform: 'capitalize',
                      }}
                    />
                  </Box>
                )}

              {annotation.tags && annotation.tags.length > 0 && (
                <Box>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    Tags:
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={0.5}
                    flexWrap='wrap'
                    sx={{ gap: 0.5 }}
                  >
                    {annotation.tags.slice(0, 3).map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size='small'
                        variant='outlined'
                        sx={{
                          fontSize: '0.7rem',
                          borderColor: '#232536',
                          color: '#232536',
                          height: '24px',
                        }}
                      />
                    ))}
                    {annotation.tags.length > 3 && (
                      <Chip
                        label={`+${annotation.tags.length - 3} more`}
                        size='small'
                        variant='outlined'
                        sx={{
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          borderColor: 'text.secondary',
                          height: '24px',
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>

            {/* Date info */}
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block' }}
            >
              Created:{' '}
              {createdDate ? createdDate.toLocaleDateString() : 'Unknown'}
              {/* {updatedDate &&
                createdDate &&
                updatedDate.getTime() !== createdDate.getTime() && (
                  <> • Updated: {updatedDate.toLocaleDateString()}</>
                )} */}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      {!readOnly && (
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Delete Annotation</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete this annotation? This action cannot
              be undone.
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: annotation.highlightColor || '#fff3cd',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant='body2' sx={{ fontStyle: 'italic' }}>
                "{annotation.selectedText?.substring(0, 100)}..."
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant='outlined'
              sx={{
                borderColor: '#232536',
                color: '#232536',          
                '&:hover': {
                  borderColor: '#3a3e5a',
                  color: '#3a3e5a',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color='error'
              variant='contained'
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default React.memo(AnnotationCard)
